import { v4 as uuidv4 } from "uuid";
import { atomFamily } from "jotai/utils";
import { atomWithQuery } from "jotai-tanstack-query";
import {
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  where,
} from "firebase/firestore";

import { getDownloadURL, ref } from "firebase/storage";
import axios from "axios";
import { customFirestore, customStorage } from "../../firebase";
import {
  type CompanyData,
  type EnvVar,
  type InputKey,
  type InteractionLog,
  type RunData,
  type RunDependency,
  type RunExecutionData,
  type TestSuiteData,
  type TestSuiteRunExecutionData,
  type TestSuiteRunRecordingData,
} from "../../utils/types";
import { type FirebaseError } from "firebase/app";
import {
  getGptDriverInput,
  getImageDimensions,
  getInputValues,
  parseMarkdownTemplate,
  getRunTemplateTimestamp,
  prepareCachedFlow,
  trackSentryException,
  mergeLaunchParams,
} from "../../utils/helpers";
import { tiptapJsonToMarkdown } from "../../components/markdown-test-editor/markdown-conversion";
import deepEqual from "fast-deep-equal";
import {
  appetizeNetworkLogsArraySchema,
  appetizeCloneNetworkLogsArraySchema,
} from "@/utils/networkLogSchemas";
import { getGlobalSettingsQueryOptions } from "@/utils/global-settings";
import JSZip from "jszip";

const testSuiteDataAtom = atomFamily((suiteId: string) => {
  return atomWithQuery((get) => ({
    queryKey: ["getTestSuiteData", suiteId],
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    retry: 0,
    queryFn: async ({ queryKey: [, suiteId] }) => {
      const testSuiteRef = await getDoc(
        doc(customFirestore, "custom-test-suite-runs", suiteId as string)
      );
      if (testSuiteRef.exists()) {
        const data = testSuiteRef.data() as TestSuiteData;
        return {
          ...data,
          metaData: data.metaData ?? {},
          id: testSuiteRef.id,
          stealthMode: data.stealthMode ?? false,
        } as TestSuiteData;
      } else {
        throw new Error("Test suite doesn't exist");
      }
    },
  }));
});

// Step-authored tests are stored as a single top-level orderedList of
// listItems. Markdown-authored tests have free-form top-level content —
// headings, paragraphs, bulletLists, etc. The cloud-run trigger sometimes
// flattens heading nodes into paragraphs when copying a test into the
// suite-run doc, so we can't only look for `#` in the rendered markdown.
// Instead, treat the doc as markdown if either (a) the structure has any
// top-level node that isn't an orderedList, or (b) the rendered output
// contains an ATX heading line. The first catches the flattened case; the
// second is a safety net for unexpected shapes.
const docHasNonStepStructure = (doc: any): boolean => {
  if (doc == null || doc.type !== "doc") return false;
  const content = doc.content ?? [];
  if (content.length === 0) return false;
  return content.some((node: any) => node?.type !== "orderedList");
};

const renderedHasAtxHeading = (markdown: string): boolean =>
  /^#{1,6}\s+\S/m.test(markdown);

const buildGlobalTestTaskFromCommands = ({
  commands,
  envVars,
  runInputValues,
  runId,
}: {
  commands: string | undefined;
  envVars: EnvVar[];
  runInputValues: Array<{ key: string; value: string }>;
  runId?: string;
}): { rendered: string; template: string } | undefined => {
  if (commands == null) return undefined;
  let parsedDoc: any;
  try {
    parsedDoc = JSON.parse(commands);
  } catch {
    return undefined;
  }
  const markdown = tiptapJsonToMarkdown(parsedDoc);
  if (markdown.length === 0) return undefined;
  if (!docHasNonStepStructure(parsedDoc) && !renderedHasAtxHeading(markdown)) {
    return undefined;
  }
  const rendered = parseMarkdownTemplate({
    markdown,
    envVars,
    runInputValues,
    // Seed with an attempt-scoped {{timestamp}} so re-renders of this run's
    // global task (atom refetch, baseline-save alignment, ...) all agree on the
    // value, keeping cached commands templatizable. Execution retries re-render
    // this from globalTestTaskTemplate with a fresh Unix-seconds timestamp.
    currentTemplateTimestamp: { current: getRunTemplateTimestamp({ runId }) },
    randomValuesTemplates: { current: {} },
  });
  return { rendered, template: markdown };
};

const getRuns = async ({
  suiteId,
  runId,
  parentRunData,
  organisationId,
  forRecording,
  envVars = [],
}: {
  suiteId: string;
  runId: string;
  parentRunData: RunData;
  organisationId: string;
  forRecording: boolean;
  envVars?: EnvVar[];
}) => {
  const dependenciesQuery = query(
    collection(
      customFirestore,
      "custom-test-suite-runs",
      suiteId,
      "runs",
      runId,
      "dependencies"
    )
  );

  const device = parentRunData.device;
  const os = parentRunData.os;

  const dependenciesSnap = await getDocs(dependenciesQuery);

  const dependencies: RunExecutionData[] = dependenciesSnap.empty
    ? []
    : (
        await Promise.all(
          dependenciesSnap.docs.map(async (dependency, index) => {
            const dependencyData = dependency.data() as RunDependency;

            const dependencyPromptSteps = getGptDriverInput(
              dependencyData.commands
            );
            // Auto-review refines globalTestTask without touching the tiptap commands,
            // so step IDs alone aren't a sufficient cache identity on retries.
            const dependencyHasReviewLoopOverride =
              (dependencyData.reviewLoopOverrideGlobalTestTask?.length ?? 0) > 0;
            const { allowCachedRun, cachedData } = await prepareCachedFlow(
              dependencyPromptSteps,
              dependencyData.cache?.data ?? [],
              !forRecording &&
                dependencyData.cache?.enabled &&
                !dependencyHasReviewLoopOverride,
              parentRunData.platform.toLowerCase(),
              organisationId
            );

            const settings = {
              launchParams: dependencyData.settings?.launchParams ?? [],
              preRequests:
                dependencyData.settings?.preRequests?.map((preRequest) => ({
                  ...preRequest,
                  delayBeforeRequest: preRequest.delayBeforeRequest ?? 0,
                })) ?? [],
              teardownRequests:
                dependencyData.settings?.teardownRequests ?? [],
              inputKeys: dependencyData.settings?.inputKeys ?? [],
            };

            return {
              id: dependency.id,
              device,
              os,
              testId: dependencyData.custom_test_id,
              promptSteps: dependencyPromptSteps,
              cache: {
                enabled: allowCachedRun,
                data: cachedData,
              },
              settings,
              platform: dependencyData.platform,
              orientation: dependencyData.orientation,
              locale: dependencyData.locale,
              order: dependencyData.order,
              startedTimeInEpoch: dependencyData.startedTimeInEpoch,
              runData: dependencyData,
              dependencyInputValues: dependencyData.dependencyInputValues,
            } as RunExecutionData;
          })
        )
      ).sort((a, b) => a.order - b.order);

  const parentRunPromptSteps = getGptDriverInput(parentRunData.commands);
  // Auto-review refines globalTestTask without touching the tiptap commands,
  // so step IDs alone aren't a sufficient cache identity on retries.
  const hasReviewLoopOverride =
    (parentRunData.reviewLoopOverrideGlobalTestTask?.length ?? 0) > 0;
  const { allowCachedRun, cachedData } = await prepareCachedFlow(
    parentRunPromptSteps,
    parentRunData.cache?.data ?? [],
    !forRecording && parentRunData.cache?.enabled && !hasReviewLoopOverride,
    parentRunData.platform.toLowerCase(),
    organisationId
  );

  const parentInputValues =
    parentRunData?.dependencyInputValues?.[parentRunData.custom_test_id!];

  const globalInputValues = parentRunData?.dependencyInputValues?.global ?? [];

  const parsedInputKeys: InputKey[] =
    parentRunData.settings?.inputKeys?.map((inputKey) => {
      const parentInputValue = parentInputValues?.find(
        (inputValue) => inputValue.key === inputKey.key
      );
      const globalInputData = globalInputValues?.find(
        (inputValue) => inputValue.key === inputKey.key
      );

      return {
        ...inputKey,
        ...(parentInputValue != null && {
          value: parentInputValue.value,
        }),
        ...(globalInputData != null && {
          value: globalInputData.value,
        }),
      };
    }) ?? [];

  const settings = {
    launchParams: parentRunData.settings?.launchParams ?? [],
    preRequests:
      parentRunData.settings?.preRequests?.map((preRequest) => ({
        ...preRequest,
        delayBeforeRequest: preRequest.delayBeforeRequest ?? 0,
      })) ?? [],
    teardownRequests: parentRunData.settings?.teardownRequests ?? [],
    inputKeys: parsedInputKeys,
  };

  const parentRun: RunExecutionData = {
    id: parentRunData.id,
    device,
    os,
    promptSteps: parentRunPromptSteps,
    testId: parentRunData.custom_test_id,
    cache: {
      enabled: allowCachedRun,
      data: cachedData,
    },
    settings,
    order: dependencies.length + 1,
    platform: parentRunData.platform,
    orientation: parentRunData.orientation,
    locale: parentRunData.locale,
    startedTimeInEpoch: parentRunData.startedTimeInEpoch,
    runData: parentRunData,
    dependencyInputValues: parentRunData?.dependencyInputValues,
  };

  const runs = [...dependencies, parentRun];

  if (!forRecording) {
    runs.forEach((run, runIndex) => {
      const runInputValues = getInputValues({
        currentRun: run,
        nextRunsToExecute: runs.slice(runIndex + 1),
        envVars,
      });
      const globalTestTask = buildGlobalTestTaskFromCommands({
        commands: run.runData?.commands,
        envVars,
        runInputValues,
        runId: run.id,
      });
      if (globalTestTask != null) {
        run.globalTestTask = globalTestTask.rendered;
        run.globalTestTaskTemplate = globalTestTask.template;
      }
      // The FAPI auto-review loop persists a refined version of the
      // abstract task to the run doc after analyzing a previous failure.
      // When present, prefer it over the recomputed value above so the
      // vision agent runs against the refined definition on the retry.
      // No template variant: refined output is pre-rendered and there's
      // no clean placeholder form to recover.
      const reviewLoopOverride =
        run.runData?.reviewLoopOverrideGlobalTestTask;
      if (reviewLoopOverride != null && reviewLoopOverride.length > 0) {
        run.globalTestTask = reviewLoopOverride;
        run.globalTestTaskTemplate = reviewLoopOverride;
      }
    });
  }

  return { runs } as TestSuiteRunExecutionData;
};

const testRunRecordingDataAtom = atomFamily(
  ({ suiteId, runId }: { suiteId: string; runId: string }) => {
    return atomWithQuery((get) => {
      const testSuiteDataQuery = get(testSuiteDataAtom(suiteId));

      return {
        queryKey: ["getTestRunExecutionDataAtom", runId, suiteId],
        refetchOnMount: false,
        refetchOnWindowFocus: false,
        enabled: testSuiteDataQuery.data != null,
        queryFn: async ({ queryKey: [, runId, suiteId] }) => {
          try {
            const runSnap = await getDoc(
              doc(
                customFirestore,
                "custom-test-suite-runs",
                suiteId as string,
                "runs",
                runId as string
              )
            );

            if (runSnap.exists()) {
              const parentRunData = {
                ...(runSnap.data() as RunData),
                id: runSnap.id,
              };
              const runExecutionData = await getRuns({
                parentRunData,
                suiteId: suiteId as string,
                runId: runId as string,
                organisationId: (testSuiteDataQuery.data as TestSuiteData)
                  .organisationId,
                forRecording: true,
              });

              const parentRun = runExecutionData.runs?.at(-1)!;

              let logs: string[] = [];
              if (parentRun.runData!.logsPath != null) {
                const downloadUrl = await getDownloadURL(
                  ref(customStorage, parentRun.runData!.logsPath)
                );

                const response = await axios.get(downloadUrl);
                logs = response.data;
              } else if (parentRun.runData!.sessionId != null) {
                try {
                  const downloadUrl = await getDownloadURL(
                    ref(
                      customStorage,
                      `deviceLogs/${
                        parentRun.runData!.sessionId
                      }/device_log.zip`
                    )
                  );
                  console.log("downloadUrl", downloadUrl);

                  const response = await axios.get(downloadUrl, {
                    responseType: "arraybuffer",
                  });
                  console.log("response", response);
                  const zipData = response.data;
                  const zip = await JSZip.loadAsync(zipData);
                  const fileNames = Object.keys(zip.files);
                  console.log("fileNames", fileNames);
                  if (fileNames.length > 0) {
                    const firstFileName = fileNames[0];
                    const fileContent = await zip.files[firstFileName].async(
                      "string"
                    );
                    logs = fileContent
                      .split("\n")
                      .filter((line) => line.length > 0);
                  }
                } catch {
                  // File doesn't exist, logs remain empty
                }
              }

              let networkLogs: any[] = [];
              if (parentRun.runData!.networkLogsPath != null) {
                // Appetize format: JSON array of { json: <HAR entry> } objects
                const downloadUrl = await getDownloadURL(
                  ref(customStorage, parentRun.runData!.networkLogsPath)
                );

                const response = await axios.get(downloadUrl);
                let rawData: unknown;
                if (typeof response.data === "string") {
                  try {
                    rawData = JSON.parse(response.data);
                  } catch (error) {
                    console.error("Error parsing JSON from string:", error);
                    trackSentryException(error);
                  }
                } else {
                  rawData = response.data;
                }
                if (rawData != null) {
                  console.log("rawData", rawData);
                  const parsed =
                    appetizeNetworkLogsArraySchema.safeParse(rawData);
                  if (parsed.success) {
                    networkLogs = parsed.data;
                  } else {
                    console.error(
                      "Error parsing network logs schema:",
                      parsed.error
                    );
                    trackSentryException(parsed.error);
                  }
                }
              } else if (parentRun.runData!.sessionId != null) {
                // Appetize Clone format: zipped JSON validated with Zod schema
                try {
                  const downloadUrl = await getDownloadURL(
                    ref(
                      customStorage,
                      `deviceLogs/${
                        parentRun.runData!.sessionId
                      }/network_capture.zip`
                    )
                  );

                  const response = await axios.get(downloadUrl, {
                    responseType: "arraybuffer",
                  });
                  const zipData = response.data;
                  const zip = await JSZip.loadAsync(zipData);
                  const fileNames = Object.keys(zip.files);
                  if (fileNames.length > 0) {
                    const firstFileName = fileNames[0];
                    const fileContent = await zip.files[firstFileName].async(
                      "string"
                    );
                    try {
                      // Network logs are concatenated JSON objects (not a JSON array),
                      // e.g. {"flow_id":"..."}{"flow_id":"..."} — split them before parsing
                      const rawData = JSON.parse(
                        `[${fileContent.replace(/}\s*\{/g, "},{")}]`
                      );
                      const parsed =
                        appetizeCloneNetworkLogsArraySchema.safeParse(rawData);
                      if (parsed.success) {
                        networkLogs = parsed.data;
                      } else {
                        console.error(
                          "Error parsing network logs schema:",
                          parsed.error
                        );
                        trackSentryException(parsed.error);
                      }
                    } catch (error) {
                      console.error("Error parsing network logs JSON:", error);
                      trackSentryException(error);
                    }
                  }
                } catch {
                  // File doesn't exist, networkLogs remain empty
                }
              }

              console.log("networkLogs", networkLogs);

              let interactionLogs: InteractionLog[] = [];
              if (parentRun.runData!.interactionLogsPath != null) {
                const downloadUrl = await getDownloadURL(
                  ref(customStorage, parentRun.runData!.interactionLogsPath)
                );

                const response = await axios.get(downloadUrl);
                interactionLogs = response.data as InteractionLog[];

                const imageUrls = await Promise.all(
                  interactionLogs.map(async (interactionLog) => {
                    if (interactionLog.screenshotUrl != null) {
                      return interactionLog.screenshotUrl;
                    }
                    return await getDownloadURL(
                      ref(customStorage, interactionLog.screenshot)
                    );
                  })
                );
                let scaleFactor = null;
                if (
                  imageUrls.length > 0 &&
                  parentRun.runData?.dimensions != null
                ) {
                  const imageSize = await getImageDimensions(imageUrls.at(0));
                  scaleFactor =
                    imageSize.width / parentRun.runData.dimensions.width;
                }

                interactionLogs = interactionLogs.map(
                  (interactionLog, index) => {
                    let parsedUIElements =
                      interactionLog.serverExecutionParams?.uiElements;
                    if (scaleFactor != null && parsedUIElements != null) {
                      parsedUIElements = parsedUIElements.map((element) => ({
                        ...element,
                        bounds: {
                          x: Math.ceil(element.bounds.x / scaleFactor),
                          y: Math.ceil(element.bounds.y / scaleFactor),
                          width: Math.ceil(element.bounds.width / scaleFactor),
                          height: Math.ceil(
                            element.bounds.height / scaleFactor
                          ),
                        },
                        id: uuidv4(),
                      }));
                    }

                    return {
                      ...interactionLog,
                      screenshot: imageUrls[index],
                      ...(interactionLog.serverExecutionParams != null && {
                        serverExecutionParams: {
                          ...interactionLog.serverExecutionParams,
                          uiElements: parsedUIElements,
                        },
                      }),
                    };
                  }
                );
              }

              return {
                runs: runExecutionData.runs,
                recording: {
                  logs,
                  networkLogs,
                  interactionLogs,
                },
              } as TestSuiteRunRecordingData;
            } else {
              throw new Error("Test run doesn't exist");
            }
          } catch (e) {
            trackSentryException(e);
            const firebaseError = e as FirebaseError;
            if (firebaseError.code === "permission-denied") {
              location.reload();
            } else {
              throw e;
            }
          }
        },
      };
    });
  },
  (a, b) => a.suiteId === b.suiteId && a.runId === b.runId
);

const testRunExecutionDataAtom = atomFamily(
  ({ suiteId, runId }: { suiteId: string; runId: string }) => {
    return atomWithQuery((get) => {
      const testSuiteDataQuery = get(testSuiteDataAtom(suiteId));
      const organisationId = (
        testSuiteDataQuery.data as TestSuiteData | undefined
      )?.organisationId;
      // Pulled in so we can hydrate `globalTestTask` from markdown-authored
      // tests at run-data assembly time (mirrors the editor's
      // `setMarkdownGlobalTask`). Without envVars, template substitution
      // would be skipped on the cloud-run path.
      const companyDataQuery =
        organisationId != null ? get(companyDataAtom(organisationId)) : null;

      return {
        queryKey: [
          "getTestRunExecutionDataAtom",
          runId,
          suiteId,
          organisationId,
        ],
        refetchOnMount: false,
        refetchOnWindowFocus: false,
        enabled:
          testSuiteDataQuery.data != null && companyDataQuery?.data != null,
        queryFn: async ({ queryKey: [, runId, suiteId] }) => {
          try {
            const runSnap = await getDoc(
              doc(
                customFirestore,
                "custom-test-suite-runs",
                suiteId as string,
                "runs",
                runId as string
              )
            );

            if (runSnap.exists()) {
              const parentRunData = {
                ...(runSnap.data() as RunData),
                id: runSnap.id,
              };
              const runExecutionData = await getRuns({
                suiteId: suiteId as string,
                runId: runId as string,
                parentRunData,
                organisationId: (testSuiteDataQuery.data as TestSuiteData)
                  .organisationId,
                forRecording: false,
                envVars: companyDataQuery?.data?.settings.envVars ?? [],
              });

              // Merge launch params from all runs, parent takes priority on key conflicts
              const parentRun = runExecutionData.runs.at(-1)!;
              parentRun.settings.launchParams = mergeLaunchParams(
                runExecutionData.runs
              );

              return runExecutionData;
            } else {
              throw new Error("Test run doesn't exist");
            }
          } catch (e) {
            trackSentryException(e);
            const firebaseError = e as FirebaseError;
            if (firebaseError.code === "permission-denied") {
              location.reload();
            } else {
              throw e;
            }
          }
        },
      };
    });
  },
  (a, b) => a.suiteId === b.suiteId && a.runId === b.runId
);

const companyDataAtom = atomFamily((organisationId?: string) => {
  return atomWithQuery((get) => {
    return {
      queryKey: ["getCompanyData", organisationId],
      refetchOnMount: false,
      refetchOnWindowFocus: false,
      queryFn: async ({ queryKey: [, organisationId], client }) => {
        const companyQuery = query(
          collection(customFirestore, "companies"),
          where("id", "==", organisationId)
        );
        const companyDoc = (await getDocs(companyQuery)).docs.at(0)!;
        const companyData = companyDoc.data();

        const settingsRef = doc(
          customFirestore,
          "settings",
          organisationId as string
        );
        const settingsSnap = await getDoc(settingsRef);
        if (settingsSnap.exists()) {
          const settingsData = settingsSnap.data();
          let enablePhysicalDeviceExecutionToggle = false;
          const shouldDisplayPhysicalDeviceExecutionToggle =
            settingsData.shouldDisplayPhysicalDeviceExecutionToggle ?? false;

          if (shouldDisplayPhysicalDeviceExecutionToggle) {
            const globalSettings = await client.ensureQueryData(
              getGlobalSettingsQueryOptions(organisationId as string)
            );

            enablePhysicalDeviceExecutionToggle =
              globalSettings.enablePhysicalDeviceExecutionToggle;
          }

          const parsedCompanyData: CompanyData = {
            name: companyDoc.id,
            templates: companyData.templates ?? [],
            audios: companyData.audios ?? [],
            editorExecutionModel:
              companyData.model_version?.prompting ?? "split-gpt54-seed",
            serverExecutionModel:
              companyData.model_version.execution ?? "split-gpt54-seed",
            fallbackModel:
              companyData.model_version?.fallback ?? "claude-agent-testing",
            visionModel: companyData.model_version?.vision ?? "claude-agent",
            organisationId: organisationId as string,
            usageNonVision: companyData.usage_non_vision ?? {},
            usageWithVision: companyData.usage_with_vision ?? {},
            usageLocalization: companyData.usage_localization ?? {},
            usageSDKWithVision: companyData.usage_sdk_with_vision ?? {},
            usageSDKNonVision: companyData.usage_sdk_non_vision ?? {},
            billing: companyData.billing,
            testsBackend: companyData.testsBackend,
            subCompanies: [],
            settings: {
              authRequired: settingsData.authRequired === "true",
              envVars: settingsData.envVars ?? [],
              tags: settingsData.tags ?? [],
              displayLogs: settingsData.displayLogs ?? true,
              displayNetworkLogs: !!settingsData.displayNetworkLogs,
              ...(settingsData.proxy?.length > 0 && {
                proxy: settingsData.proxy,
              }),
              displayNetworkLogsOnEditor:
                settingsData.proxy?.length > 0
                  ? false
                  : settingsData.displayNetworkLogsOnEditor ?? false,
              ...(settingsData.proxyAppetizeClone && {
                proxyAppetizeClone: settingsData.proxyAppetizeClone,
              }),
              shouldResetActionHistory:
                settingsData.shouldResetActionHistory ?? false,
              disableVirtualKeyboard:
                settingsData.disableVirtualKeyboard ?? false,
              splitTextWhenTyping: settingsData.splitTextWhenTyping ?? true,
              numberOfRetriesOnFailure:
                settingsData.numberOfRetriesOnFailure ?? 0,
              increased_timeout: settingsData.increased_timeout === true,
              utilizeFullTextAnnotation:
                settingsData.utilizeFullTextAnnotation ?? false,
              dynamicActionHistoryEnabled:
                settingsData.dynamicActionHistoryEnabled ?? false,
              enableSortingOCR: settingsData.enableSortingOCR ?? false,
              enableActionHistoryCut:
                settingsData.enableActionHistoryCut ?? false,
              enableCommandSuggestions:
                settingsData.enableCommandSuggestions ?? false,
              removeOverlappingText:
                settingsData.removeOverlappingText ?? false,
              popupDetectionEnabled:
                settingsData.popupDetectionEnabled ?? false,
              runHistoryEnabled: settingsData.runHistoryEnabled ?? true,
              sameStepPerformedLimit: settingsData.sameStepPerformedLimit ?? 0,
              sameScreenOccurredLimit:
                settingsData.sameScreenOccurredLimit ?? 0,
              sameScreenMessageEnabled:
                settingsData.sameScreenMessageEnabled ?? false,
              shortenCommandStringEnabled:
                settingsData.shortenCommandStringEnabled ?? false,
              shortenCommandStringFromTopEnabled:
                settingsData.shortenCommandStringFromTopEnabled ?? false,
              numberOfCachedScreensCompared:
                settingsData.numberOfCachedScreensCompared ?? 0,
              waitForStableScreenTime:
                settingsData.waitForStableScreenTime ?? 3000,
              ocrProvider: settingsData.ocrProvider ?? "gcp",
              modelResponseDoubleCheckEnabled:
                settingsData.modelResponseDoubleCheckEnabled ?? false,
              testSettingsEnabled: settingsData.testSettingsEnabled ?? false,
              cachingEnabled: settingsData.cachingEnabled ?? false,
              enableFoldersFeature: settingsData.enableFoldersFeature ?? false,
              enableSessionsFeature:
                settingsData.enableSessionsFeature ?? false,
              enableIssuesScreen: settingsData.enableIssuesScreen ?? false,
              defaultSessionConfig: {
                deviceAndroid: settingsData.deviceAndroid,
                osAndroid: settingsData.androidVersion,
                osIos: settingsData.iOSVersion,
                deviceIos: settingsData.deviceIOS,
                language: settingsData.language ?? "en_US",
                orientation: "portrait" as "portrait" | "landscape",
                region: settingsData.region ?? "automatic",
              },
              aggressiveCachingEnabled:
                settingsData.aggressiveCachingEnabled ?? false,
              forceStrictCaching: settingsData.forceStrictCaching ?? false,
              cachingSyncIndexEnabled:
                settingsData.cachingSyncIndexEnabled ?? false,
              enableAppetizeCloneEditor:
                settingsData.enableAppetizeCloneEditor ??
                settingsData.enableAppetizeClone ??
                false,
              originalEnableAppetizeCloneEditor:
                settingsData.enableAppetizeCloneEditor ??
                settingsData.enableAppetizeClone ??
                false,
              enableMarkdownEditor:
                settingsData.enableMarkdownEditor === true,
              // Mirror the FAPI auto-review eligibility toggles so the
              // run-history defer logic in use-execute-steps can tell whether a
              // run will be auto-reviewed (and thus shouldn't be recorded until
              // the verdict lands). Without these the flags are undefined ->
              // treated as off -> run history written immediately.
              enableAutoFailureReviewLoop:
                settingsData.enableAutoFailureReviewLoop === true,
              enableAutoSuccessVerification:
                settingsData.enableAutoSuccessVerification === true,
              enableSdkSessionsAutoReview:
                settingsData.enableSdkSessionsAutoReview === true,
              enableAppetizeCloneCloud:
                settingsData.enableAppetizeCloneCloud ??
                settingsData.enableAppetizeClone ??
                false,
              enableCommandsAIFallback:
                settingsData.enableCommandsAIFallback ?? true,
              enableAssertVisibleAIFallback:
                settingsData.enableAssertVisibleAIFallback ?? false,
              enableAppetizeCloneAndroid:
                settingsData.enableAppetizeCloneAndroid ?? true,
              enableAppetizeCloneIOS:
                settingsData.enableAppetizeCloneIOS ?? true,
              enableAudioFiles: settingsData.enableAudioFiles ?? false,
              popupDetectionParameters:
                settingsData.popupDetectionParameters ?? {},
              templatesDetectionParameters:
                settingsData.templatesDetectionParameters ?? {},
              disabledButtonsDetectionEnabled:
                settingsData.disabledButtonsDetectionEnabled ?? false,
              disabledButtonsDetectionParameters:
                settingsData.disabledButtonsDetectionParameters ?? {},
              useAppetizeCommands: settingsData.useAppetizeCommands ?? true,
              autoGrantPermissions: settingsData.autoGrantPermissions ?? false,
              customSlideDurations: settingsData.customSlideDurations ?? {},
              enableTestMentions: settingsData.enableTestMentions ?? false,
              ignoreStealthForPlatform:
                settingsData.ignoreStealthForPlatform ?? false,
              fallbackInfrastructure: settingsData.fallbackInfrastructure ?? {
                enabled: false,
              },
              typeUsingKeypress: settingsData.typeUsingKeypress ?? true,
              useContentDescForLocalAppetizeTap:
                settingsData.useContentDescForLocalAppetizeTap ?? false,
              enableOfflineModeCommand:
                settingsData.enableOfflineModeCommand ?? false,
              displayLocalizationsSettings:
                settingsData.displayLocalizationsSettings ?? false,
              localizations: {
                ...settingsData.localizations,
                keywordsExceptions:
                  settingsData.localizations?.keywordsExceptions ?? [],
                subjectsProsemirrorExceptions:
                  settingsData.localizations?.subjectsProsemirrorExceptions?.map(
                    (item) => JSON.parse(item)
                  ) ?? [],
                localizedSubjectsProsemirrorExceptions:
                  Object.keys(
                    settingsData.localizations
                      ?.localizedSubjectsProsemirrorExceptions ?? {}
                  ).reduce((prev, key) => {
                    return {
                      ...prev,
                      [key]: JSON.parse(
                        settingsData.localizations
                          .localizedSubjectsProsemirrorExceptions[key]
                      ),
                    };
                  }, {}) ?? {},
                subjectsExceptions:
                  settingsData.localizations?.subjectsExceptions ?? [],
                localizedSubjectsExceptions:
                  settingsData.localizations?.localizedSubjectsExceptions ?? [],
                enabled: settingsData.localizations?.enabled ?? false,
              },
              displayIntegrationsSettings:
                settingsData.displayIntegrationsSettings ?? false,
              displayJiraIntegration:
                settingsData.displayJiraIntegration ?? false,
              displayBrowserStackIntegration:
                settingsData.displayBrowserStackIntegration ?? false,
              browserStackIntegration: {
                username: settingsData.browserStackIntegration?.username ?? "",
                accessKey:
                  settingsData.browserStackIntegration?.accessKey ?? "",
                parallelDevices:
                  settingsData.browserStackIntegration?.parallelDevices ?? 1,
              },
              jiraIntegration: {
                enabled: settingsData.jiraIntegration?.enabled ?? false,
                host: settingsData.jiraIntegration?.host ?? "",
                email: settingsData.jiraIntegration?.email ?? "",
                apiToken: settingsData.jiraIntegration?.apiToken ?? "",
                projectKey: settingsData.jiraIntegration?.projectKey ?? "",
              },
              enableCodeGeneration: settingsData.enableCodeGeneration ?? false,
              enableSubCompanies: settingsData.enableSubCompanies ?? false,
              enableOptimizeAndValidateButton:
                settingsData.enableOptimizeAndValidateButton ?? false,
              enableAppDistributionIntegration:
                settingsData.enableAppDistributionIntegration ?? false,
              displayGithubIntegration:
                settingsData.displayGithubIntegration ?? false,
              githubAppInstalled:
                (settingsData.github_app_installations ?? []).length > 0,
              googleServiceAccountKeyPaths:
                settingsData.googleServiceAccountKeyPaths ?? [],
              googleOAuthFilePath: settingsData.googleOAuthFilePath,
              enablePhysicalDeviceExecutionToggle,
              uiInspector: {
                ignoredElements:
                  settingsData.uiInspector?.ignoredElements ?? [],
              },
              delayForLocalCommandsDisabled:
                settingsData.delayForLocalCommandsDisabled ?? false,
              enableAppHandbook: settingsData.enableAppHandbook ?? false,
              appHandbook: settingsData.appHandbook ?? "",
            },
          };

          if (parsedCompanyData.settings.enableSubCompanies) {
            const subCompanies = companyData.subCompanies ?? [];

            const subCompaniesQuery = query(
              collection(customFirestore, "companies"),
              where("id", "in", subCompanies)
            );
            const subCompaniesSnap = await getDocs(subCompaniesQuery);
            parsedCompanyData.subCompanies = subCompaniesSnap.docs.map(
              (doc) => ({
                id: doc.data().id,
                name: doc.id,
              })
            );
          }

          return parsedCompanyData;
        }
      },
      enabled: organisationId != null,
      refetchOnReconnect: false,
    };
  });
}, deepEqual);

export {
  testRunExecutionDataAtom,
  testSuiteDataAtom,
  testRunRecordingDataAtom,
  companyDataAtom,
};
